import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Banner,
  Button,
  Card,
  Select,
  Text,
} from "@shopify/polaris";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getLinkedPaypalShop } from "../../../redux/store/dashboard/Action";
import { getStoreDetails } from "../../../redux/store/find_by_id/Action";
import {
  digitalSyncStatus,
  requestToDeleteAccount,
  requestToSetDeafultAccount,
} from "../../../redux/store/paypalSettings/Action";
import { getAllCarrierTable } from "../../../redux/store/courierMapping/Action";
import StorePageFrame from "./StorePageFrame";

const sectionRowStyle = {
  display: "grid",
  gridTemplateColumns: "240px minmax(0, 1fr)",
  gap: "20px",
  alignItems: "start",
};

const sectionLeftStyle = {
  minWidth: "220px",
  paddingTop: "10px",
};

const SettingsLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const storeDetailsState = useSelector((store) => store.requireStoreDetailsRedycer);
  const dashboardState = useSelector((store) => store.requiredStoreDashboardData);
  const paypalSettingsState = useSelector((store) => store.requiredPaypalSettingReducer);
  const courierMappingState = useSelector((store) => store.requiredCourierMappingReducer);

  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [notifyBuyer, setNotifyBuyer] = useState(true);
  const [orderDigestFrequency, setOrderDigestFrequency] = useState("Monthly");
  const [failedDigestEnabled, setFailedDigestEnabled] = useState(true);
  const [failedDigestFrequency, setFailedDigestFrequency] = useState("Weekly");

  const linkedPaypalAccounts = Array.isArray(dashboardState?.linked_paypal)
    ? dashboardState.linked_paypal
    : [];
  const firstAccount = linkedPaypalAccounts[0] || null;

  const storeDetails = storeDetailsState?.findById || {};
  const activePlan = storeDetails?.planName || "Free";
  const totalTokens = Number(storeDetails?.totalTokens ?? 0);
  const digitalSyncEnabled = Boolean(
    paypalSettingsState?.digital_sync?.isSuccess
      ? paypalSettingsState?.digital_sync?.data?.digitalSync
      : storeDetails?.digitalSync
  );
  const courierMappingEnabled = Boolean(storeDetails?.courrierMapping);

  const carrierRules = Array.isArray(courierMappingState?.allCarriers)
    ? courierMappingState.allCarriers
    : [];

  useEffect(() => {
    dispatch(getStoreDetails(id));
    dispatch(getLinkedPaypalShop(id));
    dispatch(getAllCarrierTable(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (paypalSettingsState?.delete?.isSuccess || paypalSettingsState?.default?.isSuccess) {
      dispatch(getLinkedPaypalShop(id));
    }
  }, [dispatch, id, paypalSettingsState?.default?.isSuccess, paypalSettingsState?.delete?.isSuccess]);

  useEffect(() => {
    if (
      storeDetails?.message === "Actual error message" ||
      dashboardState?.linked_paypal?.message === "Actual error message" ||
      courierMappingState?.allCarriers?.message === "Actual error message"
    ) {
      navigate("/logout");
    }
  }, [courierMappingState?.allCarriers?.message, dashboardState?.linked_paypal?.message, navigate, storeDetails?.message]);

  const formatActivationDate = useMemo(() => {
    const value = storeDetails?.chargeFirstAcceptedAt || storeDetails?.creationDate || storeDetails?.createdAt;
    if (!value) {
      return "N/A";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }
    return date.toLocaleDateString("en-GB");
  }, [storeDetails?.chargeFirstAcceptedAt, storeDetails?.createdAt, storeDetails?.creationDate]);

  return (
    <StorePageFrame title="Settings" gap="20px">

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                PayPal settings
              </Text>
            </div>
            <Card sectioned>
              <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <Text as="p" variant="headingSm">PayPal authorization</Text>
                    <Text as="p" tone="subdued">
                      The merchant must connect or add accounts from PalSync inside Shopify Admin.
                    </Text>
                  </div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid #e5e7eb", paddingTop: "12px" }}>
                  <Text as="p">{firstAccount?.payerId || firstAccount?.payerEmail || "N/A"}</Text>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Badge>
                      {(firstAccount?.orderCount || 0).toLocaleString("en-US")} orders
                    </Badge>
                    <Badge status="success">Active</Badge>
                    {firstAccount ? (
                      <Button
                        plain
                        destructive
                        loading={paypalSettingsState?.delete?.isRequest}
                        onClick={() => dispatch(requestToDeleteAccount(id, firstAccount.id))}
                      >
                        Disconnect
                      </Button>
                    ) : null}
                    {firstAccount && !firstAccount?.default ? (
                      <Button
                        loading={paypalSettingsState?.default?.isRequest}
                        onClick={() => dispatch(requestToSetDeafultAccount(id, firstAccount.id))}
                      >
                        Set default
                      </Button>
                    ) : null}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                Digital product
              </Text>
            </div>
            <Card sectioned>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Text as="p" variant="headingSm">Digital orders</Text>
                    <Badge status={digitalSyncEnabled ? "success" : "info"}>
                      {digitalSyncEnabled ? "On" : "Off"}
                    </Badge>
                  </div>
                  <Button
                    onClick={() =>
                      dispatch(digitalSyncStatus({ state: !digitalSyncEnabled, shopId: id }))
                    }
                    loading={paypalSettingsState?.digital_sync?.isRequest}
                  >
                    {digitalSyncEnabled ? "Turn off" : "Turn on"}
                  </Button>
                </div>
                <Text as="p" color="subdued">
                  The orders delivered digitally without physical tracking can be synced as digital orders.
                </Text>
                <Banner title="Info" status="info">
                  <p>If you turn on digital order syncing, orders without tracking details will sync as digital orders.</p>
                </Banner>
              </div>
            </Card>
          </div>

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                Courier Mapping
              </Text>
              <Text as="p" color="subdued">
                Courier mapping allows changing carrier names from Shopify to PayPal while syncing.
              </Text>
            </div>
            <Card sectioned>
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Text as="p" variant="headingSm">Map by courier name</Text>
                    <Badge status={courierMappingEnabled ? "success" : "info"}>
                      {courierMappingEnabled ? "On" : "Off"}
                    </Badge>
                  </div>
                </div>
                {carrierRules.length === 0 ? (
                  <Text as="p" color="subdued">No Rules found</Text>
                ) : (
                  carrierRules.slice(0, 5).map((rule, idx) => (
                    <div key={rule?.id || idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Text as="p">
                        {rule?.carrierToReplaceId || rule?.carrier_to_replace_id || "N/A"} {"->"} {rule?.carrier || rule?.paypalCarrierId || "N/A"}
                      </Text>
                      <Badge status="success">Active</Badge>
                    </div>
                  ))
                )}
                <Button plain disabled>
                  + Add new rule
                </Button>
              </div>
            </Card>
          </div>

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                Tokens
              </Text>
            </div>
            <Card sectioned>
              <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                <Text as="p" variant="headingSm">Tokens</Text>
                <Text as="p" variant="headingLg">{totalTokens}</Text>
                <Text as="p" color="subdued">Token left</Text>
                <Text as="p">Every request uses up to 1 token so include older orders in one go.</Text>
              </div>
            </Card>
          </div>

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                Subscription
              </Text>
            </div>
            <Card sectioned>
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <Text as="p" variant="headingSm">Active plan: {activePlan}</Text>
                    <Badge status="success">Active</Badge>
                  </div>
                  <Button onClick={() => navigate(`/store/${id}/plan`)}>View plans</Button>
                </div>
                <Text as="p">Activation Date: {formatActivationDate}</Text>
              </div>
            </Card>
          </div>

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                Language
              </Text>
            </div>
            <Card sectioned>
              <Select
                label="Language"
                labelHidden
                options={[
                  { label: "English", value: "en" },
                  { label: "Français", value: "fr" },
                  { label: "German", value: "de" },
                  { label: "Español", value: "es" },
                  { label: "Dutch", value: "nl" },
                  { label: "Polish", value: "pl" },
                  { label: "Italian", value: "it" },
                  { label: "Português", value: "pt" },
                ]}
                value={selectedLanguage}
                onChange={setSelectedLanguage}
              />
            </Card>
          </div>

          <div style={sectionRowStyle}>
            <div style={sectionLeftStyle}>
              <Text as="h2" variant="headingMd">
                Email notifications
              </Text>
              <Text as="p" color="subdued">
                PalSync sends important email messages for syncing events or errors.
              </Text>
            </div>
            <Card sectioned>
              <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Text as="p" variant="headingSm">Order digest</Text>
                      <Badge status={notifyBuyer ? "success" : "critical"}>{notifyBuyer ? "On" : "Off"}</Badge>
                    </div>
                    <Button onClick={() => setNotifyBuyer((prev) => !prev)}>
                      {notifyBuyer ? "Turn off" : "Turn on"}
                    </Button>
                  </div>
                  <Select
                    label="Order digest frequency"
                    options={[
                      { label: "Daily", value: "Daily" },
                      { label: "Weekly", value: "Weekly" },
                      { label: "Monthly", value: "Monthly" },
                    ]}
                    value={orderDigestFrequency}
                    onChange={setOrderDigestFrequency}
                  />
                </div>
                <div style={{ border: "1px solid #e5e7eb", borderRadius: "10px", padding: "12px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Text as="p" variant="headingSm">Failed order digest</Text>
                      <Badge status={failedDigestEnabled ? "success" : "critical"}>
                        {failedDigestEnabled ? "On" : "Off"}
                      </Badge>
                    </div>
                    <Button onClick={() => setFailedDigestEnabled((prev) => !prev)}>
                      {failedDigestEnabled ? "Turn off" : "Turn on"}
                    </Button>
                  </div>
                  <Select
                    label="Failed order digest frequency"
                    options={[
                      { label: "Daily", value: "Daily" },
                      { label: "Weekly", value: "Weekly" },
                      { label: "Monthly", value: "Monthly" },
                    ]}
                    value={failedDigestFrequency}
                    onChange={setFailedDigestFrequency}
                  />
                </div>
              </div>
            </Card>
          </div>
    </StorePageFrame>
  );
};

export default SettingsLayout;
